import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    FiArrowLeft,
    FiCheckCircle,
    FiClock,
    FiFileText,
    FiLoader,
    FiPrinter,
    FiRefreshCw,
    FiSlash,
} from "react-icons/fi";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    useHistory,
    useParams,
} from "react-router-dom";

import {
    cancelDebitNote,
    confirmDebitNote,
    fetchDebitNoteById,
    selectedDebitNoteSelector,
    debitNoteLoadingSelector,
    debitNoteErrorSelector,
} from "../../../../../../store/billing/debitNotes/debitNoteSlice";

import DebitNoteItemsTable from "./components/DebitNoteItemsTable";
import DebitNoteSummary from "./components/DebitNoteSummary";

import "./DebitNotes.css";

const toNumber = (
    value,
    fallback = 0
) => {
    const number =
        Number(
            value
        );

    return Number.isFinite(
        number
    )
        ? number
        : fallback;
};

const roundMoney = (
    value
) =>
    Math.round(
        (
            toNumber(
                value
            ) +
            Number.EPSILON
        ) *
            100
    ) / 100;

const formatCurrency = (
    value
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            style:
                "currency",

            currency:
                "INR",

            minimumFractionDigits:
                2,

            maximumFractionDigits:
                2,
        }
    ).format(
        roundMoney(
            value
        )
    );

const formatDate = (
    value,
    includeTime = false
) => {
    if (!value) {
        return "-";
    }

    const date =
        new Date(
            value
        );

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return new Intl.DateTimeFormat(
        "en-IN",
        includeTime
            ? {
                  day:
                      "2-digit",

                  month:
                      "short",

                  year:
                      "numeric",

                  hour:
                      "2-digit",

                  minute:
                      "2-digit",
              }
            : {
                  day:
                      "2-digit",

                  month:
                      "short",

                  year:
                      "numeric",
              }
    ).format(
        date
    );
};

const formatLabel = (
    value
) =>
    String(
        value ||
            "-"
    )
        .replace(
            /_/g,
            " "
        )
        .toLowerCase()
        .replace(
            /\b\w/g,
            (
                character
            ) =>
                character.toUpperCase()
        );

const getDebitAmount = (
    debitNote
) =>
    roundMoney(
        debitNote
            ?.totals
            ?.debitAmount ??
        debitNote
            ?.debitAmount ??
        0
    );

const getSupplierName = (
    debitNote
) =>
    debitNote
        ?.supplierSnapshot
        ?.supplierName ||
    debitNote
        ?.supplierSnapshot
        ?.vendorName ||
    debitNote
        ?.supplier
        ?.supplierName ||
    debitNote
        ?.supplier
        ?.name ||
    "Supplier";

const getSupplierMobile = (
    debitNote
) =>
    debitNote
        ?.supplierSnapshot
        ?.mobile ||
    debitNote
        ?.supplier
        ?.mobile ||
    "-";

const getSupplierEmail = (
    debitNote
) =>
    debitNote
        ?.supplierSnapshot
        ?.email ||
    debitNote
        ?.supplier
        ?.email ||
    "-";

const getErrorMessage = (
    error,
    fallback
) =>
    error?.message ||
    error?.payload
        ?.message ||
    error?.payload ||
    error?.response
        ?.data
        ?.message ||
    fallback;

const DebitNoteViewPage = () => {
    const dispatch =
        useDispatch();

    const history =
        useHistory();

    const {
        id,
    } =
        useParams();

    const debitNote =
        useSelector(
            selectedDebitNoteSelector
        );

    const loading =
        useSelector(
            debitNoteLoadingSelector
        ) || {};

    const errors =
        useSelector(
            debitNoteErrorSelector
        ) || {};

    const [
        localError,
        setLocalError,
    ] = useState("");

    const [
        actionLoading,
        setActionLoading,
    ] = useState("");

    const status =
        String(
            debitNote
                ?.status ||
                "DRAFT"
        ).toUpperCase();

    const items =
        useMemo(
            () =>
                Array.isArray(
                    debitNote
                        ?.items
                )
                    ? debitNote.items
                    : [],
            [
                debitNote,
            ]
        );

    const loadDebitNote =
        useCallback(
            async () => {
                if (!id) {
                    return;
                }

                try {
                    setLocalError(
                        ""
                    );

                    await dispatch(
                        fetchDebitNoteById(
                            id
                        )
                    ).unwrap();
                } catch (
                    error
                ) {
                    setLocalError(
                        getErrorMessage(
                            error,
                            "Unable to load debit note."
                        )
                    );
                }
            },
            [
                dispatch,
                id,
            ]
        );

    useEffect(
        () => {
            loadDebitNote();
        },
        [
            loadDebitNote,
        ]
    );

    const handleBack =
        () => {
            history.push(
                "/billing/debit-notes"
            );
        };

    const handleConfirm =
        async () => {
            if (
                !id ||
                status !==
                    "DRAFT"
            ) {
                return;
            }

            const approved =
                window.confirm(
                    `Confirm debit note ${
                        debitNote
                            ?.debitNoteNumber ||
                        ""
                    }?\n\nInventory will be reduced and supplier payable will be adjusted.`
                );

            if (
                !approved
            ) {
                return;
            }

            try {
                setLocalError(
                    ""
                );

                setActionLoading(
                    "CONFIRM"
                );

                await dispatch(
                    confirmDebitNote(
                        id
                    )
                ).unwrap();

                await loadDebitNote();
            } catch (
                error
            ) {
                setLocalError(
                    getErrorMessage(
                        error,
                        "Unable to confirm debit note."
                    )
                );
            } finally {
                setActionLoading(
                    ""
                );
            }
        };

    const handleCancel =
        async () => {
            if (
                !id ||
                status !==
                    "DRAFT"
            ) {
                return;
            }

            const reason =
                window.prompt(
                    `Enter cancellation reason for ${
                        debitNote
                            ?.debitNoteNumber ||
                        "this debit note"
                    }:`
                );

            if (
                reason ===
                null
            ) {
                return;
            }

            const normalizedReason =
                String(
                    reason ||
                        ""
                ).trim();

            if (
                normalizedReason.length <
                3
            ) {
                setLocalError(
                    "Cancellation reason must contain at least 3 characters."
                );

                return;
            }

            try {
                setLocalError(
                    ""
                );

                setActionLoading(
                    "CANCEL"
                );

                await dispatch(
                    cancelDebitNote({
                        debitNoteId:
                            id,

                        cancellationReason:
                            normalizedReason,
                    })
                ).unwrap();

                await loadDebitNote();
            } catch (
                error
            ) {
                setLocalError(
                    getErrorMessage(
                        error,
                        "Unable to cancel debit note."
                    )
                );
            } finally {
                setActionLoading(
                    ""
                );
            }
        };

    const handlePrint =
        () => {
            window.print();
        };

    if (
        loading.details &&
        !debitNote
    ) {
        return (
            <div className="debit-note-view-page">
                <div className="debit-note-empty">
                    <FiLoader className="debit-note-spinner" />

                    <h3>
                        Loading debit note...
                    </h3>
                </div>
            </div>
        );
    }

    return (
        <div className="debit-note-view-page">
            <header className="debit-note-view-header">
                <div className="debit-note-list-heading">
                    <button
                        type="button"
                        className="debit-note-button debit-note-no-print"
                        onClick={
                            handleBack
                        }
                    >
                        <FiArrowLeft />
                    </button>

                    <span className="debit-note-list-heading-icon">
                        <FiFileText />
                    </span>

                    <div>
                        <small>
                            DEBIT NOTE
                        </small>

                        <h1>
                            {debitNote
                                ?.debitNoteNumber ||
                                "Debit Note"}
                        </h1>

                        <p>
                            Against purchase bill{" "}
                            <strong>
                                {debitNote
                                    ?.purchaseBillNumber ||
                                    "-"}
                            </strong>
                        </p>
                    </div>
                </div>

                <div className="debit-note-view-actions debit-note-no-print">
                    <button
                        type="button"
                        className="debit-note-button"
                        disabled={
                            loading.details
                        }
                        onClick={
                            loadDebitNote
                        }
                    >
                        <FiRefreshCw
                            className={
                                loading.details
                                    ? "debit-note-spinner"
                                    : ""
                            }
                        />

                        Refresh
                    </button>

                    <button
                        type="button"
                        className="debit-note-button"
                        onClick={
                            handlePrint
                        }
                    >
                        <FiPrinter />

                        Print
                    </button>

                    {status ===
                        "DRAFT" && (
                        <>
                            <button
                                type="button"
                                className="debit-note-button debit-note-button-success"
                                disabled={
                                    Boolean(
                                        actionLoading
                                    )
                                }
                                onClick={
                                    handleConfirm
                                }
                            >
                                {actionLoading ===
                                "CONFIRM" ? (
                                    <FiLoader className="debit-note-spinner" />
                                ) : (
                                    <FiCheckCircle />
                                )}

                                Confirm
                            </button>

                            <button
                                type="button"
                                className="debit-note-button debit-note-button-danger"
                                disabled={
                                    Boolean(
                                        actionLoading
                                    )
                                }
                                onClick={
                                    handleCancel
                                }
                            >
                                {actionLoading ===
                                "CANCEL" ? (
                                    <FiLoader className="debit-note-spinner" />
                                ) : (
                                    <FiSlash />
                                )}

                                Cancel
                            </button>
                        </>
                    )}
                </div>
            </header>

            {(localError ||
                errors.details ||
                errors.confirm ||
                errors.cancel) && (
                <div className="debit-note-alert">
                    {localError ||
                        errors.details ||
                        errors.confirm ||
                        errors.cancel}
                </div>
            )}

            <section className="debit-note-summary-grid">
                <div className="debit-note-summary-card">
                    <span>
                        Status
                    </span>

                    <strong>
                        <span
                            className={`debit-note-status debit-note-status-${status.toLowerCase()}`}
                        >
                            {status ===
                            "CONFIRMED" ? (
                                <FiCheckCircle />
                            ) : status ===
                              "CANCELLED" ? (
                                <FiSlash />
                            ) : (
                                <FiClock />
                            )}

                            {formatLabel(
                                status
                            )}
                        </span>
                    </strong>

                    <small>
                        Document status
                    </small>
                </div>

                <div className="debit-note-summary-card">
                    <span>
                        Supplier
                    </span>

                    <strong>
                        {getSupplierName(
                            debitNote
                        )}
                    </strong>

                    <small>
                        {getSupplierMobile(
                            debitNote
                        )}
                    </small>
                </div>

                <div className="debit-note-summary-card">
                    <span>
                        Purchase Bill
                    </span>

                    <strong>
                        {debitNote
                            ?.purchaseBillNumber ||
                            "-"}
                    </strong>

                    <small>
                        {formatDate(
                            debitNote
                                ?.purchaseBillDate
                        )}
                    </small>
                </div>

                <div className="debit-note-summary-card debit-note-summary-card-amount">
                    <span>
                        Debit Amount
                    </span>

                    <strong>
                        {formatCurrency(
                            getDebitAmount(
                                debitNote
                            )
                        )}
                    </strong>

                    <small>
                        {formatDate(
                            debitNote
                                ?.debitNoteDate
                        )}
                    </small>
                </div>
            </section>

            <section className="debit-note-view-card">
                <div className="debit-note-view-info-grid">
                    <div>
                        <span>
                            Debit Note Number
                        </span>

                        <strong>
                            {debitNote
                                ?.debitNoteNumber ||
                                "-"}
                        </strong>
                    </div>

                    <div>
                        <span>
                            Debit Note Date
                        </span>

                        <strong>
                            {formatDate(
                                debitNote
                                    ?.debitNoteDate
                            )}
                        </strong>
                    </div>

                    <div>
                        <span>
                            Financial Year
                        </span>

                        <strong>
                            {debitNote
                                ?.financialYear ||
                                "-"}
                        </strong>
                    </div>

                    <div>
                        <span>
                            Reason
                        </span>

                        <strong>
                            {formatLabel(
                                debitNote
                                    ?.reason
                            )}
                        </strong>
                    </div>

                    <div>
                        <span>
                            Supplier Mobile
                        </span>

                        <strong>
                            {getSupplierMobile(
                                debitNote
                            )}
                        </strong>
                    </div>

                    <div>
                        <span>
                            Supplier Email
                        </span>

                        <strong>
                            {getSupplierEmail(
                                debitNote
                            )}
                        </strong>
                    </div>
                </div>

                {debitNote
                    ?.reasonDetails && (
                    <div
                        style={{
                            marginTop:
                                "12px",
                        }}
                    >
                        <strong>
                            Reason Details
                        </strong>

                        <p
                            style={{
                                color:
                                    "#475569",

                                fontSize:
                                    "11px",
                            }}
                        >
                            {
                                debitNote
                                    .reasonDetails
                            }
                        </p>
                    </div>
                )}
            </section>

            <section className="debit-note-view-card">
                <div
                    style={{
                        display:
                            "flex",

                        justifyContent:
                            "space-between",

                        alignItems:
                            "center",

                        marginBottom:
                            "12px",
                    }}
                >
                    <div>
                        <h3
                            style={{
                                margin:
                                    0,

                                fontSize:
                                    "14px",
                            }}
                        >
                            Returned Items
                        </h3>

                        <p
                            style={{
                                margin:
                                    "3px 0 0",

                                color:
                                    "#64748b",

                                fontSize:
                                    "10px",
                            }}
                        >
                            Items returned to the supplier.
                        </p>
                    </div>

                    <span>
                        {items.length}{" "}
                        {items.length ===
                        1
                            ? "item"
                            : "items"}
                    </span>
                </div>

                <DebitNoteItemsTable
                    items={
                        items
                    }
                />
            </section>

            <DebitNoteSummary
                debitNote={
                    debitNote
                }
            />

            {(debitNote?.notes ||
                debitNote?.internalNotes) && (
                <section className="debit-note-view-card">
                    <h3>
                        Notes
                    </h3>

                    {debitNote
                        ?.notes && (
                        <p>
                            {
                                debitNote
                                    .notes
                            }
                        </p>
                    )}

                    {debitNote
                        ?.internalNotes && (
                        <p>
                            Internal:{" "}
                            {
                                debitNote
                                    .internalNotes
                            }
                        </p>
                    )}
                </section>
            )}

            {status ===
                "CANCELLED" && (
                <section className="debit-note-view-card">
                    <div className="debit-note-alert">
                        <div>
                            <strong>
                                Debit Note Cancelled
                            </strong>

                            <div>
                                {debitNote
                                    ?.cancellationReason ||
                                    "-"}
                            </div>

                            <small>
                                {formatDate(
                                    debitNote
                                        ?.cancelledAt,
                                    true
                                )}
                            </small>
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
};

export default DebitNoteViewPage;